
import {RawLocation, Location} from "vue-router";
import {TypeType} from "@/components/molecules/IconBtns";
import {isObject, isString} from "@/utils/TypeUtil";
import {defineComponent, PropType} from "vue";
import {PopoverPlacement} from "element-ui/types/popover";

/**
 * アイコンのみのボタン
 */
export default defineComponent({
  name: 'IconBtn',
  props: {
    /** ツールチップに表示する文字列 */
    tooltip: {
      type: String,
      default: '',
    },
    /** ツールチップの表示位置 */
    tooltipPlacement: {
      type: String as PropType<PopoverPlacement>,
      default: 'bottom'
    },
    /** ツールチップが表示されるまでの遅延を無くす場合 true そうでない場合 false */
    tooltipNoDelay: {
      type: Boolean,
      default: false
    },
    /** ツールチップが表示されるまでの遅延を短くする場合 true そうでない場合 false */
    tooltipShortDelay: {
      type: Boolean,
      default: false
    },
    /** Removes the ability to click or target the component. */
    disabled: {
      type: [Boolean, null] as PropType<boolean | null>,
      default: null,
    },
    /** ボタンをクリックした際の遷移先ロケーション */
    to: {
      type: [String, Object, null] as PropType<RawLocation | null>,
      default: null,
    },
    /** Alt キーを押しながらボタンをクリックした際の遷移先ロケーション、または to を改変する関数 */
    toOnAltKey: {
      type: [String, Object, Function, null] as PropType<RawLocation | string | ((to: Location) => Location) | null>,
      default: null,
    },
    /** ボタンの種別 */
    type: {
      type: [String, null] as PropType<string | null>,
      default: null,
    },
    /** ボタンの順番 */
    order: {
      type: [String, Number, null] as PropType<number | string | null>,
      default: null,
    },
    /** ボタンのアイコン */
    icon: {
      type: [String, null] as PropType<string | null>,
      default: null,
    },
    /** ボタンのアイコン色 */
    iconColor: {
      type: [String, null] as PropType<string | null>,
      default: null,
    },
    /** ボタンの背景色 */
    color: {
      type: [String, null] as PropType<string | null>,
      default: null,
    },
    /**  コンポーネントを extra small にします */
    xSmall: {
      type: Boolean,
      default: false,
    },
    /** コンポーネントを small にします */
    small: {
      type: Boolean,
      default: false,
    },
    /** コンポーネントを large にします */
    large: {
      type: Boolean,
      default: false,
    },
    /** コンポーネントを extra large にします */
    xLarge: {
      type: Boolean,
      default: false,
    },
    /** ボタンの影を削除します */
    depressed: {
      type: Boolean,
      default: false,
    },
    /** 非アイコンモード */
    unicon: {
      type: Boolean,
      default: false,
    },
    /** 小ぶりな見た目 */
    dense: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'click',
    'click-native'
  ],
  computed: {
    /**
     * 表示に使用するツールチップ文字列
     */
    tooltipView(): string {
      if(this.tooltip) {
        return this.tooltip;
      }

      switch(this.type) {
        case 'edit':
        case 'edit-content':
          return '編集';
        case 'view':
          return '閲覧';
        case 'delete':
          return '削除';
        case 'disuse':
          return '利用停止';
        case 'add':
          return '新規';
        default:
          return '';
      }
    },

    /**
     * 表示に使用するアイコン
     */
    iconView(): string {
      if(this.icon) {
        return this.icon;
      }

      switch(this.type) {
        case 'edit':
          return 'mdi-square-edit-outline';
        case 'edit-content':
          return 'mdi-pencil';
        case 'view':
          return 'el-icon-view';
        case 'delete':
        case 'disuse':
          return 'mdi-trash-can-outline';
        case 'add':
          return 'mdi-plus';
        default:
          return 'mdi-help-circle';
      }
    },

    /**
     * 表示に使用するアイコン色
     */
    iconColorView(): null | string {
      if(this.iconColor) {
        return this.iconColor;
      }

      switch(this.type) {
        case 'edit':
        case 'edit-content':
        case 'view':
        case 'add':
          return null;
        case 'delete':
          return 'red';
        case 'disuse':
          return 'red lighten-1';
        default:
          return this.icon ? null : 'red';
      }
    },

    /**
     * v-btn に渡すアイコンを取得します
     */
    iconSize() {
      if(this.xSmall) {
        return 'x-small';
      } else if(this.small) {
        return 'small';
      } else if(this.large) {
        return 'large';
      } else if(this.xLarge) {
        return 'x-large';
      } else {
        return 'default';
      }
    },

    /**
     * ツールチップが開くまでの遅延時間 (ms)
     */
    tooltipOpenDelay(): number {
      if(this.tooltipNoDelay) {
        return 0;
      } else if(this.tooltipShortDelay) {
        return 500;
      } else {
        return 1000;
      }
    },
  },
  methods: {
    /**
     * ボタンのクリックイベントを処理します
     *
     * @param e MouseEvent
     * @private
     */
    handleClick(e: MouseEvent) {
      if(this.to) {
        this.$router.push(this.to);
      }

      this.$emit('click', e);
    },

    /**
     * ボタンのネイティブクリックイベントを処理します
     *
     * @param e MouseEvent
     * @private
     */
    handleNativeClick(e: MouseEvent) {
      if(!this.to) {
        return;
      }

      if(e.altKey && this.toOnAltKey) {
        e.preventDefault();
        const toOnAltKey = this.toOnAltKey;
        if(typeof toOnAltKey === 'function') {
          let {location} = this.$router.resolve(this.to);
          if (location) {
            location = toOnAltKey(location);
          }
          if(location) {
            this.$router.replace(location);
          }
        } else if(isString(toOnAltKey) || isObject(toOnAltKey)) {
          this.$router.replace(toOnAltKey as RawLocation);
        }
      }
    }
  }
});
